import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ColorToken, IconComponent } from '@cumlaude/shared-components-icons';
import { TooltipDirective } from '@cumlaude/shared-components-overlays';
import { MENU_NAVIGATION_TRACKER } from '@cumlaude/shared-components-menu';
import { NavigatieRij } from '../navigatie-model';

/**
 * Eén rij in de verticale hoofdnavigatie. Presentatie-only: geen app-specifieke service-injecties
 * (alleen de optionele, generieke MENU_NAVIGATION_TRACKER, zoals TopLevelMenuComponent die ook
 * gebruikt), zodat dit bestand op zichzelf staat en later herbruikbaar is.
 *
 * Navigeren gebeurt via een gewone routerLink naar `rij().item.url`. Voor een groep resolvet
 * Angular's eigen redirectTo daarna naar de eerste subpagina, en NavigatieUitklapService klapt de
 * tak vanzelf open omdat de nieuwe url er een ancestor bij krijgt (zie navigatie-model.ancestorUrls)
 * -- er is dus geen aparte "expand on click"-code nodig voor het open-zetten van de tak zelf. Het
 * los openen/sluiten via de chevron, en het eventueel weer uitklappen van een ingeklapte rail bij
 * een titelklik, lopen via outputs (zodat NavigatieUitklapService alleen in HoofdnavigatieComponent
 * hoeft te worden geïnjecteerd) en moeten de klik op de link annuleren resp. laten doorlopen.
 */
@Component({
	selector: 'app-navigatie-rij',
	templateUrl: './navigatie-rij.component.html',
	styleUrls: ['./navigatie-rij.component.scss'],
	imports: [RouterLink, IconComponent, TooltipDirective],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigatieRijComponent {
	private readonly navigationTracker = inject(MENU_NAVIGATION_TRACKER, { optional: true });

	rij = input.required<NavigatieRij>();

	/** Ingeklapte rail: toont alleen het icoon (niveau 0) met een tooltip in plaats van titel + chevron. */
	ingeklapt = input(false);

	chevronGeklikt = output<string>();

	/** Emit bij een klik op de titel van een rij met children, zodat de ingeklapte rail (indien van
	 * toepassing) weer kan uitklappen -- zie HoofdnavigatieComponent. */
	titelMetChildrenGeklikt = output<string>();

	/**
	 * In de ingeklapte (icon-only) rail zijn subpagina's niet zichtbaar, dus moet het icoon van een
	 * groep die de actieve pagina bevat er ook als "actief" uitzien -- anders is nergens meer te zien
	 * welke sectie je actief hebt staan. In de uitgeklapte rail geldt dat niet: daar toont de
	 * uitgeklapte tak zelf al welke pagina actief is, dus blijft een dichte ouder-groep neutraal.
	 */
	protected readonly toontAlsActief = computed(() => {
		const rij = this.rij();
		return rij.actief || (this.ingeklapt() && rij.bevatActief);
	});

	protected readonly iconKleur = computed((): ColorToken => (this.toontAlsActief() ? 'secondary-1' : 'typography-1'));

	onChevronClick(event: Event) {
		event.preventDefault();
		event.stopPropagation();
		this.chevronGeklikt.emit(this.rij().item.url);
	}

	onTitelClick() {
		this.navigationTracker?.trackMenuNavigation(this.rij().item.url);
		if (this.rij().hasChildren) this.titelMetChildrenGeklikt.emit(this.rij().item.url);
	}
}
